// src/api/features/products/queries.ts

import { useQuery, type QueryClient } from "@tanstack/react-query";

import { listProducts, listPublicProducts, getProduct } from "./api";

import type {
  ProductWithConfig,
  ProductListQuery,
  PaginatedProductItems,
} from "./types";

/* ======================================================
 * Query Keys
 * ====================================================== */

export const ProductsKeys = {
  all: ["products"] as const,
  list: (query: ProductListQuery = {}) =>
    ["products", "list", query] as const,
  detail: (id: number | string) =>
    ["products", "detail", String(id)] as const,
};

/* ======================================================
 * LIST hook – GET /products
 * Returns: PaginatedProductItems (unwrapped)
 * ====================================================== */

export function useProductsList(
  query: ProductListQuery = {},
  options?: { enabled?: boolean }
) {
  return useQuery<PaginatedProductItems, Error>({
    queryKey: ProductsKeys.list(query),
    queryFn: () => listProducts(query),
    enabled: options?.enabled ?? true,
    placeholderData: (previousData) => previousData,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
  });
}

export function usePublicProductsList(
  query: ProductListQuery = {},
  options?: { enabled?: boolean }
) {
  return useQuery<PaginatedProductItems, Error>({
    queryKey: [...ProductsKeys.list(query), "public"],
    queryFn: () => listPublicProducts(query),
    enabled: options?.enabled ?? true,
    placeholderData: (previousData) => previousData,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
  });
}

/* ======================================================
 * DETAIL hook – GET /products/:id
 * Returns: ProductWithConfig
 * ====================================================== */

export function useProductDetail(id: number | null) {
  const enabled = id != null;

  return useQuery<ProductWithConfig, Error>({
    queryKey: enabled
      ? ProductsKeys.detail(id as number)
      : ["products", "detail", "disabled"],
    queryFn: () => {
      if (id == null) {
        // never runs due to enabled:false, just for TS
        return Promise.reject(new Error("Product id is required"));
      }
      return getProduct(id);
    },
    enabled,
    staleTime: 60_000,
    gcTime: 10 * 60_000,
  });
}

/* ======================================================
 * Prefetch helpers (optional)
 * ====================================================== */

export function prefetchProducts(
  qc: QueryClient,
  query: ProductListQuery = {}
) {
  return qc.prefetchQuery({
    queryKey: ProductsKeys.list(query),
    queryFn: () => listProducts(query),
    staleTime: 30_000,
  });
}

export function prefetchProduct(qc: QueryClient, id: number) {
  return qc.prefetchQuery({
    queryKey: ProductsKeys.detail(id),
    queryFn: () => getProduct(id),
    staleTime: 60_000,
  });
}
